// The posts still live in the legacy WordPress install mounted at
// /konstant-blog/, so every URL WordPress hands back (post.link, the whole
// yoast_head_json graph, related_blogs, author links, and the internal links
// inside content.rendered) points at that old path. Passing those through
// verbatim republishes the legacy blog's URL space on the new site: a crawl of
// only 40 posts surfaced 69 distinct /konstant-blog/ page URLs, all duplicates
// of the /blog/ pages that replaced them. Rewrite them at the API boundary.
const LIVE_BLOG_BASE = "https://www.konstantinfo.com/blog/";

// Real files and endpoints under the WP install — those URLs still have to
// resolve, so they are left exactly as they are.
const WP_ASSET_PATH = /^(?:wp-content|wp-includes|wp-admin|wp-json|xmlrpc\.php|feed)/;

// WP archive routes with no counterpart on the new site (/blog/author/... and
// /blog/category/... both 404). Collapsing them to the blog index keeps the
// rewrite from trading a duplicate page for a broken one.
const WP_ARCHIVE_SEGMENT = /^(?:author|category|tag|page|comments|\d{4})$/;

const LEGACY_BLOG_URL = /(?:https?:\/\/[^\s"'<>()\\]+?)?\/konstant-blog\/([^\s"'<>()\\]*)/g;

/** Repoint a single string's legacy /konstant-blog/ URLs at the live /blog/ ones. */
export function rewriteLegacyBlogUrls(value: string): string {
  if (typeof value !== "string" || !value.includes("konstant-blog")) return value;
  return value.replace(LEGACY_BLOG_URL, (match, path: string) => {
    if (WP_ASSET_PATH.test(path)) return match;

    // Keep the fragment: Yoast's schema graph identifies nodes with them
    // (#breadcrumb, #primaryimage, #website, #/schema/person/<hash>).
    const hash = path.indexOf("#");
    const fragment = hash === -1 ? "" : path.slice(hash);
    const segments = (hash === -1 ? path : path.slice(0, hash))
      .split("/")
      .filter(Boolean);

    if (!segments.length) return LIVE_BLOG_BASE + fragment;
    if (WP_ARCHIVE_SEGMENT.test(segments[0]) || segments[0].startsWith("?")) {
      return LIVE_BLOG_BASE;
    }

    // /blog/ is a single-segment route (app/blog/[slug]), so only the post slug
    // survives. Deeper paths are WP attachment pages
    // (/konstant-blog/<post>/<image-slug>/) — pointing those at the post they
    // belong to beats rewriting them into a /blog/ 404.
    return `${LIVE_BLOG_BASE}${segments[0]}/${fragment}`;
  });
}

/**
 * Walk a parsed WordPress response and rewrite every legacy blog URL in it.
 * Returns the input untouched when there is nothing to rewrite so the common
 * case allocates nothing.
 */
export function rewriteLegacyBlogUrlsDeep<T>(data: T): T {
  if (typeof data === "string") return rewriteLegacyBlogUrls(data) as T;
  if (Array.isArray(data)) return data.map(rewriteLegacyBlogUrlsDeep) as T;
  if (data && typeof data === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      out[key] = rewriteLegacyBlogUrlsDeep(value);
    }
    return out as T;
  }
  return data;
}
